import { useMemo } from 'react';

import { useGetMappingBreakdownQuery, type BreakdownType } from '@/entities/dashboard/mapping';
import { useProjectSiteParams } from '@/shared/lib';

import { formatPieChartData } from '../lib/location-popup.utils';
import type { MapDataFilters } from './map-provider.interface';

interface UseLocationBreakdownParams {
  /** Every location behind the marker — a cluster asks about all of its members. */
  locationIds: string[];
  type: BreakdownType;
  /** Same filters the map's data was narrowed by — legacy cuts the breakdown too. */
  dataFilters?: MapDataFilters;
}

/**
 * Released-cargo breakdown for a marker. The endpoint always reports releases —
 * `type` only chooses whether they are grouped by product (REP) or by user (REO) —
 * which is why this is offered on the Released Cargo slice alone.
 */
export function useLocationBreakdown({ locationIds, type, dataFilters }: UseLocationBreakdownParams) {
  const { projectId, siteId } = useProjectSiteParams();

  const { data, isFetching } = useGetMappingBreakdownQuery({
    locationIds,
    type,
    projectId,
    siteId,
    organizationId: dataFilters?.organizationId,
    consignmentNumber: dataFilters?.consignmentNumber
  });

  return {
    pieData: useMemo(() => formatPieChartData(data), [data]),
    isLoading: isFetching
  };
}
